// Inspired by react-hot-toast library
import * as React from "react"
import { dispatch, listeners, memoryState } from "../functions/dispatch"
import type { State } from "../types/State"
import type { ToasterToast } from "../types/ToasterToast"

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_VALUE
  return count.toString()
}

type Toast = Omit<ToasterToast, "id">

function toast({ ...props }: Toast) {
  const id = genId()
  
  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
    
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })
  
  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open: boolean) => {
        if (!open) dismiss()
      },
    },
  })
  
  return {
    id: id,
    dismiss,
    update,
  }
}

/**
 * @group Hooks
 * @summary <blockquote>Hook for managing toast notifications.</blockquote>
 * @example
 * ```tsx
 * const { toast, dismiss } = useToast();
 * toast({ title: "Success", description: "Operation completed" });
 * ```
 */
function useToast() {
  const [state, setState] = React.useState<State>(memoryState)
  
  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])
  
  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast } 
